/*
 * Corsi — il vocabolario della pianificazione: i giorni della settimana, come si scrive una data,
 * come si generano le date di una regola e come si riconosce un conflitto. Sono funzioni pure,
 * senza chiamate di rete: i dati veri arrivano dalle API, queste servono a leggerli e a comporli.
 */

export const GIORNI = [
    { value: 'MONDAY', label: 'Lunedì', breve: 'Lun' },
    { value: 'TUESDAY', label: 'Martedì', breve: 'Mar' },
    { value: 'WEDNESDAY', label: 'Mercoledì', breve: 'Mer' },
    { value: 'THURSDAY', label: 'Giovedì', breve: 'Gio' },
    { value: 'FRIDAY', label: 'Venerdì', breve: 'Ven' },
    { value: 'SATURDAY', label: 'Sabato', breve: 'Sab' },
    { value: 'SUNDAY', label: 'Domenica', breve: 'Dom' },
];

const INDICE_GIORNO = { SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6 };

/** Il giorno della settimana di una data, come lo chiama il server. */
export const giornoDellaData = (iso) =>
    Object.keys(INDICE_GIORNO).find(g => INDICE_GIORNO[g] === new Date(`${iso}T12:00:00`).getDay()) || '';

export const nomeGiorno = (valore, breve = false) => {
    const g = GIORNI.find(x => x.value === valore);
    return g ? (breve ? g.breve : g.label) : valore;
};

export const dataBreve = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—');

const chiaveData = (d) => {
    if (typeof d === 'string') return d.slice(0, 10);
    // Data locale, non UTC: toISOString() in Italia manda al giorno prima
    const mese = String(d.getMonth() + 1).padStart(2, '0');
    const giorno = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mese}-${giorno}`;
};

/**
 * Le date di una regola di pianificazione (un giorno della settimana, un orario, un maestro e un
 * impianto) dal primo all'ultimo giorno della squadra. Serve a proporre la pianificazione prima
 * di mandarla al server: e' il server che poi tiene il calendario vero.
 */
export const generaDateRegola = (regola, dal, al) => {
    if (!dal || !al || !regola.giorno || !regola.inizio || !regola.fine) return [];
    const date = [];
    const fine = new Date(`${String(al).slice(0, 10)}T12:00:00`);
    for (let giorno = new Date(`${String(dal).slice(0, 10)}T12:00:00`); giorno <= fine; giorno.setDate(giorno.getDate() + 1)) {
        if (giorno.getDay() !== INDICE_GIORNO[regola.giorno]) continue;
        date.push({
            id: `d${chiaveData(giorno)}-${regola.inizio}-${Math.random().toString(36).slice(2, 6)}`,
            data: chiaveData(giorno),
            giorno: regola.giorno,
            inizio: regola.inizio,
            fine: regola.fine,
            maestroId: regola.maestroId,
            impiantoId: regola.impiantoId,
        });
    }
    return date;
};

/**
 * Il motivo del conflitto di una data, se c'è: stesso maestro o stesso impianto già impegnati
 * nello stesso giorno con orari che si sovrappongono, in questa o in un'altra squadra. I nomi
 * arrivano da fuori, perché maestri e impianti sono quelli veri della struttura.
 */
export const conflittoDi = (data, tutteLeDate, nomi = {}) => {
    const nomeMaestro = nomi.maestro || (id => id);
    const nomeImpianto = nomi.impianto || (id => id);
    const motivi = [];
    tutteLeDate.forEach(({ data: altra, squadra }) => {
        if (altra.id === data.id) return;
        if (altra.data !== data.data) return;
        if (!(data.inizio < altra.fine && altra.inizio < data.fine)) return;
        if (data.maestroId && altra.maestroId === data.maestroId) motivi.push(`${nomeMaestro(data.maestroId)} è già impegnato${squadra ? ` con «${squadra}»` : ''}`);
        if (data.impiantoId && altra.impiantoId === data.impiantoId) motivi.push(`${nomeImpianto(data.impiantoId)} è occupato${squadra ? ` da «${squadra}»` : ''}`);
    });
    return [...new Set(motivi)].join('; ') || null;
};

/** Tutte le date di un corso, con il nome della squadra a cui appartengono. */
export const dateDelCorso = (corso, escludiSquadraId = null) =>
    (corso.squadre || [])
        .filter(s => s.id !== escludiSquadraId)
        .flatMap(s => (s.date || []).map(d => ({ data: d, squadra: s.nome })));

/** Riepilogo delle date di una squadra: "Lun 17:00-18:00 · Mer 17:00-18:00". */
export const riepilogoDate = (date) => {
    if (!date || date.length === 0) return 'Nessuna data';
    const viste = new Map();
    date.forEach(d => viste.set(`${d.giorno}-${d.inizio}-${d.fine}`, `${nomeGiorno(d.giorno, true)} ${d.inizio}-${d.fine}`));
    return [...viste.values()].join(' · ');
};

/** I nomi distinti di un campo delle date (maestri, impianti). */
export const distinti = (date, campo, nome) =>
    [...new Set((date || []).map(d => d[campo]).filter(Boolean))].map(nome).join(', ') || '—';

/**
 * La regola settimanale che riassume un elenco di date: un giorno e un orario per ogni
 * combinazione che compare. E' quello che il server si aspetta come `weeklySchedule`.
 */
export const regolaSettimanaleDa = (date) => {
    const viste = new Map();
    (date || []).forEach(d => {
        viste.set(`${d.giorno}-${d.inizio}-${d.fine}`, { dayOfWeek: d.giorno, startTime: d.inizio, endTime: d.fine });
    });
    return [...viste.values()];
};
